import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  colors,
  useTheme,
  makeStyles,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import { DatePicker, LocalizationProvider } from "@material-ui/pickers";
import clsx from "clsx";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { apitOrganizationSamplesCounts } from "src/api/endpoints";
import { materialColors } from "src/components/pages/reports/charts/conf";
import PieChart from "./chart";
import MomentAdapter from "@material-ui/pickers/adapter/moment";
import moment from "moment";
import { localeTr } from "../../../reports/utils/helpers";
import DonutLargeSharpIcon from "@material-ui/icons/DonutLargeSharp";

const useStyles = makeStyles((theme) => ({
  iconWrapper: {
    position: "relative",
    width: 130,
    height: 130,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "& svg": {
      transition: "color 0.5s ease",
    },
    "&:hover svg.addIcon": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    "&:hover svg.donutIcon": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
  },
  addIcon: {
    fontSize: 70,
    color: "#d3d3d3",
    opacity: 0.6,
    position: "absolute",
  },
  donutIcon: {
    fontSize: 130,
    color: "#d3d3d3",
    opacity: 0.6,
  },
  createProjectLink: {
    marginTop: 10,
    color: "#5c80bc",
    textDecoration: "underline",
    fontWeight: 500,
    fontSize: "14px",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
}));

function DatePickerComponent({ setSelectedDate, selectedDate }) {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const theme = useTheme();

  useEffect(() => {
    localeTr();
    moment.locale(language.includes("tr") ? "tr" : "en");
  }, [language]);

  const handleTmpChange = (date, index) => {
    const tmp = [...selectedDate];
    if (date && date[date?.length - 1] !== "_") {
      tmp[index] = moment(new Date(date));
    }
    setSelectedDate(tmp);
  };

  return (
    <LocalizationProvider
      dateLibInstance={moment}
      dateAdapter={MomentAdapter}
      locale={language.includes("tr") ? "tr" : "en"}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            clearable
            cancelLabel={t("Cancel")}
            okLabel={t("Ok")}
            clearLabel={t("Clear")}
            format="MM/dd/yyyy"
            maxDate={new Date()}
            value={selectedDate[0]}
            onChange={(date) => handleTmpChange(date, 0)}
            label={t("Select first date")}
            renderInput={(props) => (
              <TextField style={{ width: "100%" }} {...props} />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            clearable
            cancelLabel={t("Cancel")}
            okLabel={t("Ok")}
            clearLabel={t("Clear")}
            maxDate={new Date()}
            format="MM/dd/yyyy"
            value={selectedDate[1]}
            onChange={(date) => handleTmpChange(date, 1)}
            label={t("Select second date")}
            renderInput={(props) => (
              <TextField style={{ width: "100%" }} {...props} />
            )}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}

function RunnedSamples({ classes, organization_id }) {
  const [organizationData, setOrganizationData] = useState();
  const [selectedDate, setSelectedDate] = useState([
    moment().subtract(1, "year"),
    moment(),
  ]);
  const { t, i18n } = useTranslation();

  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  const styles = useStyles();

  useEffect(() => {
    setHeight(ref?.current?.clientHeight);
    setWidth(ref?.current?.clientWidth);
  });

  useEffect(() => {
    let isMounted = true;
    const now = moment().format("YYYY-MM-DD");

    apitOrganizationSamplesCounts(
      organization_id,
      selectedDate[0].format("YYYY-MM-DD") !== now
        ? selectedDate[0].format("YYYY-MM-DD")
        : null,
      selectedDate[1].format("YYYY-MM-DD") !== now
        ? selectedDate[1].format("YYYY-MM-DD")
        : null,
      (val) => organizationManipluate(val, isMounted)
    );

    return () => {
      isMounted = false;
    };
  }, [selectedDate]);

  const organizationManipluate = async (val, mounted) => {
    const tmpOrganizationData = Object.values(val).map((items, index) => {
      const datasets = [];
      const labels = [];
      const backgroundColor = [];

      items.forEach((item) => {
        const [label, value] = Object.values(item);
        datasets.push(value);
        labels.push(label);
        backgroundColor.push(
          colors[materialColors[Math.floor(Math.random() * 17)]][
            Math.floor(Math.random() * 9) * 100
          ]
        );
      });

      return {
        title: Object.keys(val)[index],
        datasets: {
          datasets: [
            {
              label: t(Object.keys(val)[index]),
              data: datasets,
              backgroundColor: backgroundColor,
            },
          ],
          labels,
        },
      };
    });

    if (mounted) setOrganizationData(tmpOrganizationData);
  };

  if (!organizationData) {
    return <CircularProgress className={classes.spinner} />;
  }

  return organizationData.map((items, key) => {
    const total = items.datasets.datasets[0].data.reduce((a, b) => a + b, 0);
    return (
      <Grid item xs={6} key={key} ref={ref}>
        <Paper className={clsx(classes.paper, classes.runnedItem)}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <h3 style={{ textAlign: "center" }}>{t(items.title)}</h3>
            <Box data-testid="total">
              {t("Total")}: {total}
            </Box>
          </Box>

          {total === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              paddingTop={4}
              paddingBottom={6}
            >
              <Link to="/projects/create" className={styles.iconWrapper}>
                <DonutLargeSharpIcon
                  className={`${styles.donutIcon} donutIcon`}
                />
                <AddIcon className={`${styles.addIcon} addIcon`} />
              </Link>
              <Link to="/projects/create" className={styles.createProjectLink}>
                {t("Click to create project")}
              </Link>
            </Box>
          ) : (
            <PieChart
              height={height}
              width={width}
              key={key}
              datas={items.datasets}
            />
          )}

          <Box display="flex" justifyContent="space-around" alignItems="center">
            <DatePickerComponent
              setSelectedDate={setSelectedDate}
              selectedDate={selectedDate}
              language={i18n.language}
              items={items}
            />
          </Box>
        </Paper>
      </Grid>
    );
  });
}

export default RunnedSamples;
